import React, { FC, useCallback, useState, useEffect } from 'react';
import { IFileDownloadExperienceSearchResultMobile } from './types';
import {
  Button,
  DynamicPagination,
  FlexGrid,
  Sizes,
  TextBoldness,
  Typography,
  VerticalSpacing,
  VerticalSpacingSizes,
  getClassNameFactory,
} from '@dcl/ui';
import './FileDownloadExperienceSearchResultMobile.css';
import { TypographyTags } from '@dcl/ui/dist/elements/Typography';
import {
  ERROR_MESSAGE,
  INITIAL_PAGE_NUMBER,
  MAX_VISIBLE_PAGES,
  NEXT_LABEL,
  PAGINATION_LABEL,
  PREVIOUS_LABEL,
  RESULTS_PER_PAGE_LABEL,
  ROWS_PER_PAGE_LABEL_CONTENT,
  PAGE_LABEL,
  FILTER_BUTTON_TEXT_ARIA_LABEL,
  FILTER_BUTTON_TEXT,
} from '../../constants';
import useMobileViewPagination from './hooks/useMobileViewPagination';
import { FileDownloadSelector } from './components/FileDownloadSelector/FileDownloadSelector';
import { fileDownloadSelectionMockData } from '../../mock';
import { sortByColumn } from './utlis/SortByCol';
import {
  ButtonColor,
  ButtonKind,
  IconPosition,
} from '@dcl/ui/dist/elements/Button';
import FilterIcon from '../../icons/FilterIcon';
import { FilterModal } from './components/FilterModal/FilterModal';
import { IResultData } from '../../types';
import AlertIcon from '../../icons/AlertIcon';
import { filterData, transformFilterData } from '../../helper';

export const FileDownloadExperienceSearchResultMobile: FC<IFileDownloadExperienceSearchResultMobile> = (
  props
) => {
  const { columns, data, internalUseText, isLoggedIn = false } = props;
  const [pageChange, setPageChange] = useState<number>(INITIAL_PAGE_NUMBER);
  const [isOpen, setIsOpen] = useState(false);
  const [sortOptions, setSortOptions] = useState<
    typeof fileDownloadSelectionMockData
  >([]);
  const getClassName = getClassNameFactory(
    FileDownloadExperienceSearchResultMobile.displayName
  );
  const [mobileBodyData, setMobileBodyData] = useState<any[]>([]);
  const [filterOption, setFilterOption] = useState<IResultData[]>([]);
  const [pageSize, setPageSize] = useState<number>(10);
  const [tableData, setTableData] = useState<object[]>([]);
  const [sortedData, setSortedData] = useState<object[]>([]);

  useEffect(() => {
    const newData = data;
    setTableData(newData);
  }, [data]);

  useEffect(() => {
    sortedData.length > 0 && setTableData(sortedData);
    setPageChange(1);
  }, [sortedData]);

  const transformData = (columns) => {
    return columns
      .filter((column) => column.sortable === true)
      .map((column) => {
        return {
          label: column.Header,
          value: column.accessor,
        };
      });
  };

  const closeModel = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    setSortOptions(transformData(columns));
  }, [columns]);

  useEffect(() => {
    setPageChange(INITIAL_PAGE_NUMBER);
    const filterTransformedData = transformFilterData(columns, data);
    setFilterOption(filterTransformedData);
  }, [data, columns]);

  const columnData = columns;

  const {
    mobileViewPageSize,
    totalPages,
    ROWS_PER_PAGE,
    mobileViewData,
    handlePageChange,
    handlePageSizeChange,
    totalItems,
    pageIndex,
  } = useMobileViewPagination(tableData || []);

  const bodyData = mobileViewData;

  useEffect(() => {
    setMobileBodyData(bodyData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileViewData]);

  const onClickPageChange = useCallback(
    (pageChangeClick: number) => {
      handlePageChange(pageChangeClick);
      setPageChange(pageChangeClick);
    },
    [handlePageChange]
  );

  const onClickPageSizeChange = useCallback(
    (pageSizeClick: number) => {
      setPageSize(pageSizeClick);
      handlePageSizeChange(pageSizeClick);
    },
    [handlePageSizeChange]
  );

  const renderMobileViewPagination = useCallback(
    () => (
      <DynamicPagination
        previousButtonLabel={PREVIOUS_LABEL}
        nextButtonLabel={NEXT_LABEL}
        paginationLabel={PAGINATION_LABEL}
        rowsPerPageLabel={RESULTS_PER_PAGE_LABEL}
        rowsPerPageLabelContent={ROWS_PER_PAGE_LABEL_CONTENT}
        pageSize={mobileViewPageSize}
        viewingLabel={PAGE_LABEL}
        goToPageError={ERROR_MESSAGE}
        totalPages={totalPages}
        totalItems={totalItems}
        startFromPage={pageChange}
        onPageChange={onClickPageChange}
        onPageSizeChange={onClickPageSizeChange}
        numberOfVisiblePages={MAX_VISIBLE_PAGES}
        rowsPerPageValues={ROWS_PER_PAGE}
      />
    ),
    [
      onClickPageChange,
      onClickPageSizeChange,
      pageChange,
      mobileViewPageSize,
      ROWS_PER_PAGE,
      totalItems,
      totalPages,
    ]
  );

  const pageIndextext = `(Showing ${pageSize * pageIndex - pageSize + 1} to ${
    pageSize * pageIndex > totalItems ? totalItems : pageSize * pageIndex
  } of ${totalItems})`;

  const appliedFilter = (options: IResultData[]) => {
    setFilterOption(options);
    const { filteredData } = filterData(data, options);
    setTableData(filteredData);
    setPageChange(INITIAL_PAGE_NUMBER);
  };

  const getRowColor = (index: number) => {
    if (index % 2 === 0) {
      return 'light';
    } else return 'dark';
  };

  const getInternalModifier = 'internalRow';

  const getModifier = (isInternal: boolean, index: number) => {
    const modifier = isInternal ? getInternalModifier : getRowColor(index);
    return modifier;
  };

  return (
    <div className={getClassName()} data-testid="mobileViewContainer">
      <FlexGrid>
        <VerticalSpacing size={VerticalSpacingSizes.S}>
          <FlexGrid.Row className={getClassName({ descendantName: 'flexRow' })}>
            <FlexGrid.Col
              md={9}
              lg={9}
              sm={12}
              className={getClassName({ descendantName: 'searchBox' })}
            >
              <FileDownloadSelector
                fileDropdownList={sortOptions}
                filePlaceholderText={''}
                fileDropdownLabel={'Sort By'}
                fileOnDropdownValueChange={(value) => {
                  setSortedData([...sortByColumn(tableData, value)]);
                }}
                fileDropdownValue={''}
                fileMaximumVisibleItem={5}
                fileId="agencyDropdown"
              />
            </FlexGrid.Col>
          </FlexGrid.Row>
        </VerticalSpacing>
      </FlexGrid>
      <hr className={getClassName({ descendantName: 'line' })} />

      <FlexGrid>
        <FlexGrid.Row className={getClassName({ descendantName: 'flexRow' })}>
          {filterOption.length > 0 && (
            <FlexGrid.Col
              lg={12}
              sm={12}
              className={getClassName({ descendantName: 'filterBox' })}
            >
              <Button
                block
                kind={ButtonKind.BUTTON}
                color={ButtonColor.SECONDARY}
                data-testid="FileDownloadExperience-FilterIcon"
                iconPosition={IconPosition?.LEFT}
                icon={<FilterIcon />}
                aria-label={FILTER_BUTTON_TEXT_ARIA_LABEL}
                onClick={() => closeModel()}
              >
                {FILTER_BUTTON_TEXT}
              </Button>
            </FlexGrid.Col>
          )}
          <FlexGrid.Col
            lg={12}
            sm={12}
            className={getClassName({ descendantName: 'filterBox' })}
          >
            <Typography
              className={getClassName({ descendantName: 'paginationText' })}
              type={Sizes.XXTRASMALL}
              tag={TypographyTags.SPAN}
            >
              {mobileBodyData.length > 0 && pageIndextext}
            </Typography>
            {internalUseText && isLoggedIn && (
              <div
                className={getClassName({ descendantName: 'internalAlert' })}
              >
                <div
                  className={getClassName({
                    descendantName: 'internalAlertIcon',
                  })}
                >
                  {AlertIcon}
                </div>
                <div
                  className={getClassName({
                    descendantName: 'internalAlertText',
                  })}
                >
                  {internalUseText}
                </div>
              </div>
            )}
          </FlexGrid.Col>
        </FlexGrid.Row>
      </FlexGrid>

      <div className={getClassName({ descendantName: 'container' })}>
        {mobileBodyData.map((bdata: any, index) => {
          const isInternal = bdata.internal === 'true';
          const keyValue = `header_${index + 1}`;
          const conditional_modifier = getModifier(isInternal, index);
          return (
            <div
              className={getClassName({
                descendantName: 'background',
                modifiers: conditional_modifier,
              })}
              key={keyValue}
            >
              {isInternal && (
                <div
                  key={keyValue}
                  className={getClassName({ descendantName: 'alertIcon' })}
                >
                  {AlertIcon}{' '}
                </div>
              )}
              {columnData.map((heading) => {
                const isColumnInternal = !isInternal && heading.internal;
                const columnIndex = heading.accessor!.toString();
                const originalData = bdata['original' + columnIndex];
                const currentData = bdata[columnIndex];
                const isObjectData =
                  typeof originalData === 'object' ||
                  typeof currentData === 'object';
                const displayData = originalData || currentData;
                const columnSize = isColumnInternal ? 5 : 4;
                return (
                  <VerticalSpacing
                    size={VerticalSpacingSizes.S}
                    key={columnIndex}
                  >
                    <FlexGrid.Row
                      className={getClassName({
                        descendantName: 'flexRow',
                        modifiers: isColumnInternal && 'internal',
                      })}
                    >
                      <FlexGrid.Col
                        xs={columnSize}
                        sm={columnSize}
                        md={columnSize}
                        className={getClassName({
                          descendantName: 'headerGrid',
                        })}
                      >
                        <Typography
                          className={getClassName({
                            descendantName: 'heading',
                          })}
                          type={Sizes.XXTRASMALL}
                          tag={TypographyTags.SPAN}
                        >
                          {!isInternal && heading.internal && (
                            <div
                              className={getClassName({
                                descendantName: 'alertIconColumn',
                              })}
                            >
                              {AlertIcon}
                            </div>
                          )}
                          {heading.Header !== 'Action' && heading.Header + ': '}
                        </Typography>
                      </FlexGrid.Col>
                      <FlexGrid.Col
                        xs={isColumnInternal ? 7 : 8}
                        sm={isColumnInternal ? 7 : 8}
                        md={isColumnInternal ? 7 : 8}
                        className={getClassName({ descendantName: 'bodyGrid' })}
                      >
                        {isObjectData ? (
                          displayData
                        ) : (
                          <Typography
                            className={getClassName({
                              descendantName: 'bodyText',
                            })}
                            type={Sizes.XXTRASMALL}
                            tag={TypographyTags.SPAN}
                            boldness={TextBoldness.Regular}
                          >
                            {bdata['original' + columnIndex] ||
                              bdata[columnIndex]}
                          </Typography>
                        )}
                      </FlexGrid.Col>
                    </FlexGrid.Row>
                  </VerticalSpacing>
                );
              })}
            </div>
          );
        })}
        {renderMobileViewPagination()}
      </div>
      {isOpen && (
        <FilterModal
          handleCloseModal={closeModel}
          resultData={filterOption}
          handleAppliedFilter={appliedFilter}
        />
      )}
    </div>
  );
};

FileDownloadExperienceSearchResultMobile.displayName =
  'FileDownloadExperienceSearchResultMobile';
